import { useCanViewEmails } from '../../helpers/useCanViewEmails';
import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Resizable } from 're-resizable';
import { Tooltip } from '@mui/material';
import { cloneDeep } from 'lodash';
import cs from 'classnames';

import { getAllChildOrganizations, getAllOrganizations, getOrganizationRequest } from '../../api/organizationsApi';
import { ContainedButton, RolesButton } from '../modules/Buttons';
import { PaginationFooter } from '../modules/PaginationFooter';
import { DateFormatter, useDimensions } from '../../helpers';
import { ResizeIcon } from '../../assets/icons/resizeIcon';
import LinearProgress from '../modules/LinearProgress';
import styles from './ChildOrgList.module.scss';
import useDebounce from '../../helpers/useDebounce';
import { ROLES, RolesLowerCase } from '../../enums';
import { ORGANIZATION } from '../../enums/TYPES';
import SearchInput from '../modules/SearchInput';
import TrashButton from '../modules/TrashButton';
import RolePlate from '../modules/RolePlate';
import Avatar from '../modules/Avatar';
import FullPageLoader from '../modules/FullPageLoader';
import { useAppSelector } from '../../store/hooks';
import ChildOrgCard from './ChildOrgCard';
import { dateToFormattedHours } from '../../helpers/dateToFormattedHours';

enum ROLE_OPTIONS {
  ALL = 'All',
}

type ROLES_SELECTOR = ROLES | ROLE_OPTIONS;

const organizationPaginationLimit = 'organizationPaginationLimit';
const setOrganizationPaginationLimit = (val: number) => {
  return localStorage.setItem(organizationPaginationLimit, `${val}`);
};

const getOrganizationPaginationLimit = () => {
  const res = localStorage.getItem(organizationPaginationLimit);
  if (res) {
    return +res;
  } else {
    // default is 10
    return 10;
  }
};

const ChildOrgList = () => {
  const canViewEmails = useCanViewEmails();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [orgs, setOrgs] = useState<ORGANIZATION[]>([]);
  const [loading, setLoading] = useState(false);
  const { widthD } = useDimensions();

  const { myOrganization } = useAppSelector((state) => state.user);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(getOrganizationPaginationLimit());
  const [search, setSearch] = useState('');
  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onOrgSelect(null);
    setOrganizationPaginationLimit(parseInt(e.target.value, 10));
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onOrgSelect(null);
    setPage(newPage);
  };

  const debouncedValue = useDebounce<string>(search, 500);
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onOrgSelect(null);
    setSearch(e.target.value.trim());
    setPage(0);
  };

  useEffect(() => {
    if (myOrganization?.id) {
      fetchOrganizations();
    }
  }, [page, rowsPerPage, debouncedValue, myOrganization?.id]);

  const fetchOrganizations = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const limit = getOrganizationPaginationLimit();
      getAllChildOrganizations({ id: +myOrganization.id, page, limit, search })
        .then((res: { data: { documents: ORGANIZATION[]; total: number } }) => {
          setOrgs(() => res.data.documents);
          setTotal(res.data.total);
          resolve(res.data);
          const orgInFrame = res.data.documents.find((org) => org.id === selectedOrg?.id);
          if (orgInFrame) {
            onOrgSelect(cloneDeep(orgInFrame));
          } else {
            setSelectedOrg(null);
          }
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const switchModal = () => {
    onOrgSelect(null);
    setShowAddModal(!showAddModal);
  };

  const [selectedOrg, setSelectedOrg] = useState<ORGANIZATION | null>(null);

  const onOrgSelect = async (org: ORGANIZATION | null, mouseClicked?: boolean) => {
    if (loading) {
      return;
    }

    if (mouseClicked && selectedOrg?.id === org?.id) {
      setSelectedOrg(null);
      return;
    }

    if (org?.id) {
      try {
        setLoading(true);
        const { data: orgObject } = await getOrganizationRequest(org?.id);
        const users = orgObject.organization_users;
        setSelectedOrg({ ...orgObject, users });
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const [addUserModal, setAddUserModal] = useState(false);
  const switchAddUserModal = () => {
    setAddUserModal(!addUserModal);
  };

  const [searchUserText, setSearchUserText] = useState('');
  const onSearchUser = (e: any) => {
    setSearchUserText(e.target.value.toLocaleLowerCase());
  };

  const [userEditModal, setUserEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<any>(null);
  const switchUserEditModal = () => {
    setUserEditModal(!userEditModal);
  };

  const selectUserToEdit = (u: any) => {
    switchUserEditModal();
    setUserToEdit(u);
  };

  const [userDeleteModal, setUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const switchUserDeleteModal = () => {
    setUserDeleteModal(!userDeleteModal);
  };
  const selectUserToDelete = (u: any) => {
    switchUserDeleteModal();
    setUserToDelete(u);
  };

  const [currentRole, setCurrentRole] = useState<any>(ROLES.ORGANIZATION_ADMIN);
  const setRoleFilter = (role: ROLES_SELECTOR) => {
    setCurrentRole(role);
  };

  const deselectOrg = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setSelectedOrg(null);
    }
  };

  const [orgUsers, setOrgUsers] = useState(selectedOrg?.organization_users);
  useEffect(() => {
    const editedOrgUsers = selectedOrg?.organization_users.map((orgUser: any) => {
      if (orgUser.last_online) {
        const updatedLastOnline = dateToFormattedHours(orgUser.last_online);

        return {
          ...orgUser,
          last_online: updatedLastOnline,
        };
      }

      return orgUser;
    });

    setOrgUsers(editedOrgUsers);
  }, [selectedOrg?.organization_users]);

  return (
    <div className={styles.container} onClick={deselectOrg}>
      <FullPageLoader loading={loading} />
      {!selectedOrg ? (
        <div className={styles.orgSection}>
          <div className="mb-5">
            <div className={styles.pageTitle}>
              <span className={styles.span}>Child Organisations</span>
            </div>
            <div className={styles.toolbar}>
              <SearchInput value={search} className={styles.search} onChange={onSearch} />
            </div>
          </div>
          {orgs.length ? (
            <div className={cs('mb-5', styles.gridContainer)}>
              <div className={styles.listContainer}>
                <div className={styles.listHeader}>
                  <div className={styles.name}>Name</div>
                  <div className={styles.location}>Location</div>
                  <div className={styles.storage}>Storage</div>
                  <div className={styles.actionBtns}>Hours limit</div>
                </div>
                <div className={styles.orgList}>
                  {orgs.map((org) => (
                    <ChildOrgCard
                      loading={loading}
                      selectedOrg={selectedOrg}
                      onOrgSelect={onOrgSelect}
                      key={org.id}
                      data={org}
                      fetchAll={fetchOrganizations}
                      viewMode={true}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className={cs(styles.orgNotFound, 'mb-5')}>
              <h1 className={styles.title}>No organisations found.</h1>
            </div>
          )}
          <PaginationFooter
            total={total}
            page={page}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
          />
        </div>
      ) : (
        <>
          <Resizable
            enable={{
              right: selectedOrg ? true : false,
              top: false,
              bottom: false,
              left: false,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false,
            }}
            maxWidth={selectedOrg ? '60%' : '100%'}
            minWidth={'40%'}
            defaultSize={{
              width: '100%',
              height: '90%',
            }}
            handleComponent={{
              right: (
                <>
                  <div
                    style={{
                      display: selectedOrg ? 'block' : 'none',
                      right: '-20px',
                      width: '3px',
                      height: 'calc(100vh - 150px)',
                      background: '#183047',
                      borderRadius: '12px',
                      position: 'relative',
                    }}
                  >
                    <div style={{ position: 'absolute', borderRadius: '12px', left: '-21px', top: '53px' }}>
                      <ResizeIcon />
                    </div>
                  </div>
                </>
              ),
            }}
          >
            <div className={styles.orgSection}>
              <div className="mb-5" onClick={deselectOrg}>
                <div className={styles.pageTitle} onClick={deselectOrg}>
                  <span className={styles.span} onClick={deselectOrg}>
                    Child Organisations
                  </span>
                </div>
                <div className={styles.toolbar} onClick={deselectOrg}>
                  <SearchInput value={search} className={styles.search} onChange={onSearch} />
                </div>
              </div>
              {orgs.length ? (
                <div className={cs('mb-5', styles.gridContainer)} onClick={deselectOrg}>
                  <div className={styles.listContainer} onClick={deselectOrg}>
                    <div className={styles.orgListHeader} onClick={deselectOrg}>
                      <div className={styles.name} onClick={deselectOrg}>
                        Name
                      </div>
                      <div className={styles.location} onClick={deselectOrg}>
                        Location
                      </div>
                      <div className={styles.storage} onClick={deselectOrg}>
                        Storage
                      </div>
                      <div className={styles.actionBtns} onClick={deselectOrg}>
                        Hours limit
                      </div>
                    </div>
                    <div className={styles.orgList}>
                      {orgs.map((org) => (
                        <ChildOrgCard
                          loading={loading}
                          selectedOrg={selectedOrg}
                          onOrgSelect={onOrgSelect}
                          key={org.id}
                          data={org}
                          fetchAll={fetchOrganizations}
                          viewMode={true}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={cs(styles.orgNotFound, 'mb-5')}>
                  <h1 className={styles.title}>No organisations found.</h1>
                </div>
              )}
              <PaginationFooter
                total={total}
                page={page}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
              />
            </div>
          </Resizable>
          <div className={styles.usersSection}>
            <div className={cs('mb-5', styles.usersHeader)}>
              <div className={styles.pageTitle}>
                <span className={styles.span}>Organisation Admin List</span>
              </div>
              <div className={styles.toolbar}>
                <SearchInput className={styles.search} onChange={onSearchUser} />
              </div>
            </div>

            <div className={styles.listContainer}>
              <div className={styles.userListHeader}>
                <div className={styles.name}>Name</div>
                <div className={styles.role}>Role</div>
                <div className={styles.join}>Join Date</div>
                <div className={styles.login}>Last Active</div>
                {/* <div className={styles.actionBtns}>Action</div> */}
              </div>

              <div className={styles.userList}>
                {loading && <LinearProgress />}
                {orgUsers
                  ?.filter(
                    ({ user }: any) =>
                      user?.email?.toLocaleLowerCase()?.includes(searchUserText) ||
                      user?.username?.toLocaleLowerCase()?.includes(searchUserText),
                  )
                  // @ts-ignore
                  ?.filter((user) => user?.role?.title.includes(RolesLowerCase[currentRole]))
                  .map((user: any) => (
                    <div className={styles.user} key={user?.user?.id}>
                      <div className={styles.container}>
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={user?.user?.avatar_url} name={user?.user?.email} />
                            <div className={styles.name}>
                              {user?.user?.username} {canViewEmails && (<div className={styles.email}>{user?.user?.email}</div>)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!user.user.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!user.user.invitation_token ? styles.active : styles.notActive}>
                              {user.role ? <RolePlate userRole={user.role.title ? user.role.title : `${user.role}`} /> : 'No Role'}
                            </div>
                          </Tooltip>
                        </div>
                        <div className={styles.created}>{DateFormatter(user.created_at)}</div>

                        <div className={styles.login}>{user.last_online}</div>
                        {/* <div className={styles.roleAction}>
                          <img
                            onClick={() => {
                              selectUserToEdit(user.user);
                            }}
                            src={EditIcon}
                            alt="share"
                            style={{ cursor: 'pointer', marginRight: '4px' }}
                          />
                          <TrashButton
                            onClick={() => {
                              selectUserToDelete(user.user);
                            }}
                          />
                        </div> */}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChildOrgList;
